import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import styles from "./TeamForm.module.css";
import Spinner from "../Spinner/Spinner";
import Popup from "../Popup/Popup";
import { API } from "../../config";
import { useImageUpload } from "./hooks/useImageUpload";

function TeamAdd() {
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [description, setDescription] = useState("");
  const [twitter, setTwitter] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [mail, setMail] = useState("");
  const [spin, setSpin] = useState(false);
  const [show, setShow] = useState(false);

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!show) return;
    const timer = setTimeout(() => {
      setShow(false);
      navigate("/team");
    }, 2000);
    return () => clearTimeout(timer);
  }, [show]);
  const {
    selectedFile,
    imagePreview,
    uploadError,
    setUploadError,
    handleFileChange,
    uploadImage,
    resetImageState,
  } = useImageUpload();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedFile) {
      setUploadError("Please select an image");
      return;
    }

    setSpin(true);

    try {
      const imageUrl = await uploadImage(selectedFile);

      const payload = {
        name,
        position,
        image: imageUrl,
        description,
        ...(twitter && { twitter }),
        ...(linkedin && { linkedin }),
        ...(mail && { mail }),
      };

      await axios.post(`${API}/Admin/team`, payload);

      setShow(true);
      setSpin(false);
      setName("");
      setPosition("");
      setDescription("");
      setTwitter("");
      setLinkedin("");
      setMail("");
      resetImageState();
      if (fileInputRef.current) fileInputRef.current.value = "";
      event.target.reset();
    } catch (err) {
      console.error(err);
      setUploadError("Failed to save. Please try again.");
      setSpin(false);
    }
  };

  return (
    <>
      {spin && <Spinner />}
      {show && (
        <Popup show={show} switch={() => setShow(false)} message="Member added successfully" />
      )}
      <div className={styles.container}>
        <div className={styles.inner}>
          <div className={styles.upper}>
            <Link to="/">
              <span className={styles.swag}>
                Website <span className={styles.greater}>&gt;</span>
              </span>
            </Link>
            <Link to="/team">
              <span className={styles.swag}>
                Team <span className={styles.greater}>&gt;</span>
              </span>
            </Link>
            <span className={styles.req}>Add Member</span>
          </div>

          <h1 className={styles.pageTitle}>Add Team Member</h1>

          <form onSubmit={handleSubmit}>
            <div className={styles.items}>
              <div className={styles.field}>
                <p>Name *</p>
                <input
                  id={styles.gap}
                  placeholder="Full Name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className={styles.field}>
                <p>Position *</p>
                <input
                  id={styles.gap}
                  placeholder="e.g. Designer"
                  type="text"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  required
                />
              </div>

              <div className={styles.field}>
                <p>Photo *</p>
                <div
                  className={styles.imageUploadDiv}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className={styles.uploadedImage} />
                  ) : (
                    <span className={styles.uploadPlaceholder}>Click to upload photo</span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,image/svg+xml"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {uploadError && <p className={styles.error}>{uploadError}</p>}
              </div>

              <div className={styles.field}>
                <p>Bio *</p>
                <textarea
                  id={styles.gapdesc}
                  placeholder="Short bio"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                />
              </div>

              <div className={styles.field}>
                <p>Twitter (optional)</p>
                <input
                  id={styles.gap}
                  placeholder="https://twitter.com/..."
                  type="url"
                  value={twitter}
                  onChange={(e) => setTwitter(e.target.value)}
                />
              </div>

              <div className={styles.field}>
                <p>LinkedIn (optional)</p>
                <input
                  id={styles.gap}
                  placeholder="https://linkedin.com/in/..."
                  type="url"
                  value={linkedin}
                  onChange={(e) => setLinkedin(e.target.value)}
                />
              </div>

              <div className={styles.field}>
                <p>Email (optional)</p>
                <input
                  id={styles.gap}
                  placeholder="john@example.com"
                  type="email"
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                />
              </div>

              <div className={styles.buttonRow}>
                <input type="submit" className={styles.bttn} value="Add Member" />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default TeamAdd;
